import { useQuery } from '@tanstack/react-query';
import { billingApi } from '@/lib/billing';

/**
 * Compact "month-to-date AI cost" indicator for the workspace header.
 * Shows spent → cap (or "no cap"). Turns red when ≥ 80% of cap reached.
 */
export function AiCostMeter({ workspaceId }: { workspaceId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['ai-usage', workspaceId],
    queryFn: () => billingApi.aiUsage(workspaceId),
    refetchInterval: 30_000,
  });

  if (isLoading || !data) return null;

  const spent = (data.monthCents / 100).toFixed(2);
  const hasCap = data.capCents !== null;
  const pct = hasCap && data.capCents! > 0 ? data.monthCents / data.capCents! : 0;
  const danger = hasCap && pct >= 0.8;

  return (
    <div className="hidden md:flex items-center gap-2 text-xs">
      <span className="text-ink-500">AI · MTD</span>
      <span className={`num font-semibold ${danger ? 'text-warn-600' : 'text-ink-900'}`}>${spent}</span>
      {hasCap && (
        <span className={`num text-ink-500`}>/ ${(data.capCents! / 100).toFixed(0)}</span>
      )}
      {hasCap && (
        <div className="w-16 h-1.5 rounded-full bg-ink-100 overflow-hidden" title={`${(pct * 100).toFixed(0)}% of cap`}>
          <div
            className={`h-full ${danger ? 'bg-warn-500' : 'bg-accent-500'}`}
            style={{ width: `${Math.min(100, pct * 100)}%` }}
          />
        </div>
      )}
    </div>
  );
}
